import type { ComponentType, ReactNode } from 'react';
import type { SvgIconProps } from '@mui/material';
import { operatorTakesValue, type Operator, type Validation, type ValidationKind, option } from '../format/pieces';
import type { FieldWidth } from '../format/fieldWidth';
import { Infer, s, type Schema } from '../format/schema';
import type { FillInputProps, FillLabel } from './fillInput';

export type ValueKind = 'text' | 'number' | 'date' | 'option' | 'options' | 'boolean' | 'files' | 'rows';

export const hasOptions = (kind: ValueKind): boolean => {
  return kind === 'option' || kind === 'options';
};

export type Quantity = number | string;

export type Comparable = string | number | string[];

export type BuiltInCheck = 'emailFormat' | 'phoneFormat' | 'realDate' | 'number' | 'wholeNumber';

export type PaletteGroup = 'Text' | 'Choice' | 'Number, date and file' | 'Layout';

export interface PaletteItem<Settings extends object = object> {
  name: string;
  tooltip: string;
  group: PaletteGroup;
  icon: ComponentType<SvgIconProps>;
  settings?: Settings;
  validations?: Validation[];
}

type SettingsPanelPlace = 'general' | 'settings';

export interface SettingsPanelProps<Settings> {
  settings: Settings;
  onChange(settings: Partial<Settings>): void;
  builtInCheckText: string | undefined;
}

export interface FieldTypeCard<
  Settings extends Schema<object> = Schema<object>,
  Value extends Schema<unknown> = Schema<unknown>,
> {
  type: string;
  name: string;
  valueKind: ValueKind;
  settings: Settings;
  defaults: Partial<Infer<Settings>>;
  defaultWidth?(settings: Infer<Settings>): FieldWidth;
  canResize?(settings: Infer<Settings>): boolean;
  value: Value;
  emptyValue: Infer<Value>;
  hasDefaultValue: boolean;
  validations: ValidationKind[];
  operators: Operator[];
  palette: PaletteItem<Partial<Infer<Settings>>>[];
  isEmpty(value: Infer<Value>): boolean;
  quantity?(value: NonNullable<Infer<Value>>): Quantity;
  comparable?(value: NonNullable<Infer<Value>>): Comparable;
  comparableTyped?(text: string): string;
  builtInCheck?(settings: Infer<Settings> | undefined): BuiltInCheck | undefined;
  stored?(value: NonNullable<Infer<Value>>, settings: Infer<Settings>): Infer<Value>;
  FillInput(props: FillInputProps<Infer<Value>, Infer<Settings>>): ReactNode;
  fillLabel?(settings: Infer<Settings>): FillLabel;
  settingsPanel?: {
    at: SettingsPanelPlace;
    Panel(props: SettingsPanelProps<Infer<Settings>>): ReactNode;
  };
  reportsLeaving?: boolean;
}

export const defineFieldType = <Settings extends Schema<object>, Value extends Schema<unknown>>(
  card: FieldTypeCard<Settings, Value>,
): FieldTypeCard<Settings, Value> => {
  if (card.validations.includes('range') && !card.quantity)
    throw new Error(`The Field Type “${card.type}” lists “range” but has no quantity.`);
  if (card.operators.some(operatorTakesValue) && !card.comparable)
    throw new Error(`The Field Type “${card.type}” lists an operator that compares values but has no comparable.`);
  if (card.validations.includes('unique') && !card.comparable)
    throw new Error(`The Field Type “${card.type}” lists “unique” but has no comparable.`);
  return card;
};
export const optionSettings = {
  options: s.array(option),
};

export const selectedOptionIds = s
  .array(s.string())
  .refine(ids => new Set(ids).size === ids.length, 'lists an option more than once');

export const formatMessageSetting = { formatMessage: s.string().optional() };

export const noSettings = s.strictObject({});
